import {
  DataGrid,
  formatCurrency,
  formatDate,
  StatusBadge
} from '@ijoin/components';
import { useGetEnrollmentRkTransactionExports } from '@ijoin/hooks';
import { Card, CardContent, CardHeader } from '@mui/material';

import { FC } from 'react';

export const EnrollmentRkTransactionExportsSection: FC<{
  planEnrollmentId: string;
  planId: string;
}> = props => {
  const exports = useGetEnrollmentRkTransactionExports(
    props.planId,
    props.planEnrollmentId
  );

  if (!exports.data?.length) {
    return null;
  }

  return (
    <Card sx={{ p: 1 }}>
      <CardHeader
        subheader='Record keeper transactions forwarded to the annuity carrier'
        title='Record Keeper Transaction Exports'
      />
      <CardContent>
        <DataGrid
          autoHeight={false}
          columns={[
            {
              field: 'postDate',
              flex: 1,
              headerName: 'Post Date',
              minWidth: 110,
              valueFormatter: value =>
                value
                  ? formatDate(value, { format: 'MM/DD/YYYY', useUtc: true })
                  : ''
            },
            {
              field: 'transId',
              flex: 1,
              headerName: 'Trans ID',
              minWidth: 110,
              type: 'number'
            },
            {
              field: 'buySellCode',
              flex: 1,
              headerName: 'Buy/Sell',
              minWidth: 90
            },
            {
              align: 'center',
              field: 'isRoth',
              flex: 1,
              headerAlign: 'center',
              headerName: 'Roth',
              minWidth: 90,
              renderCell: params =>
                params.value ? (
                  <StatusBadge severity='info'>Roth</StatusBadge>
                ) : null
            },
            {
              field: 'totalTransAmt',
              flex: 1,
              headerName: 'Amount',
              minWidth: 120,
              type: 'number',
              valueFormatter: value =>
                value == null ? '' : formatCurrency(value)
            },
            {
              align: 'center',
              field: 'isProcessed',
              flex: 1,
              headerAlign: 'center',
              headerName: 'Processed',
              minWidth: 110,
              renderCell: params => (
                <StatusBadge severity={params.value ? 'success' : 'info'}>
                  {params.value ? 'Processed' : 'Pending'}
                </StatusBadge>
              )
            },
            {
              field: 'sentToCarrierDate',
              flex: 1,
              headerName: 'Sent To Carrier',
              minWidth: 180,
              valueFormatter: value =>
                value
                  ? formatDate(value, { format: 'MM/DD/YYYY h:mm:ss A' })
                  : ''
            },
            {
              field: 'carrierTransactionCode',
              flex: 1,
              headerName: 'Carrier Transaction',
              minWidth: 160
            },
            {
              field: 'carrierResultMessage',
              flex: 1.5,
              headerName: 'Carrier Result',
              minWidth: 160
            },
            {
              field: 'tradeIdentifier',
              flex: 1,
              headerName: 'Trade Identifier',
              minWidth: 160
            },
            {
              field: 'fileIdentifier',
              flex: 1,
              headerName: 'File Identifier',
              minWidth: 160
            },
            {
              field: 'createdAt',
              flex: 1,
              headerName: 'Created',
              minWidth: 180,
              valueFormatter: value =>
                value
                  ? formatDate(value, { format: 'MM/DD/YYYY h:mm:ss A' })
                  : ''
            }
          ]}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowId={row => row.id}
          loading={exports.isFetching}
          pageSizeOptions={[25, 50, 100]}
          pinnedColumns={{ left: ['postDate'] }}
          rows={exports.data}
          showColumnMenu
          slotProps={{
            root: {
              'data-component': 'dataGrid',
              'data-testid': 'enrollmentRkTransactionExports'
            }
          }}
          sx={{ maxHeight: 600 }}
        />
      </CardContent>
    </Card>
  );
};

EnrollmentRkTransactionExportsSection.displayName =
  'EnrollmentRkTransactionExportsSection';
